import { type RefObject, useRef } from 'react';
import type { z } from 'zod';

import { SMS_MARKETING_FEATURE_FLAG } from '@repo/common';
import type { UpdateProfileInput } from '@repo/frontend-shared';
import { DataTestIds, SocialContactType } from '@repo/frontend-shared';
import { SDSFlexbox, type SDSFlexboxGap } from '@repo/sds/SDSFlexbox';
import { Loader } from '@repo/ui/components/elements/Loader/Loader';
import { userProfileFormSchema } from '@repo/ui/constants/zod-schemas';
import { useCurrentUser } from '@repo/ui/contexts/current-user-context';
import type { FormConfig, FormRef } from '@repo/ui/fixtures/form/components/Form/Form';
import { Form } from '@repo/ui/fixtures/form/components/Form/Form';
import { getFormFieldLayoutStyle } from '@repo/ui/fixtures/form/components/FormField/FormField';
import { buildUserProfileFormFields } from '@repo/ui/fixtures/form/constants/user-profile-form-fields';
import { useDeviceType } from '@repo/ui/hooks/use-device-type';
import { useFeatureFlag } from '@repo/ui/hooks/use-feature-flag';

import { UserProfileFooter } from '../../../fixtures/userSettings/components/UserProfileFooter/UserProfileFooter';
import { UserProfileHeader } from '../../../fixtures/userSettings/components/UserProfileHeader/UserProfileHeader';
import { type ProfileSmsMarketingActions, ProfileSmsMarketingFields } from './profile-sms-marketing-fields';

import './UserProfile.scss';

type FormValues = z.infer<typeof userProfileFormSchema>;
type CurrentUser = NonNullable<ReturnType<typeof useCurrentUser>['currentUser']>;

function normalizeProfileText(value: string | null | undefined): string {
  return value ?? '';
}

function getSocialContact(currentUser: CurrentUser, type: SocialContactType): string {
  return normalizeProfileText(currentUser.socialContacts?.find(contact => contact.type === type)?.value);
}

function buildProfileForm(currentUser: CurrentUser): FormValues {
  return {
    firstName: currentUser.firstName,
    tagline: normalizeProfileText(currentUser.tagline),
    timezone: normalizeProfileText(currentUser.timezone),
    lastName: currentUser.lastName,
    position: normalizeProfileText(currentUser.position),
    company: normalizeProfileText(currentUser.company),
    description: normalizeProfileText(currentUser.bio),
    email: currentUser.email,
    linkedin: getSocialContact(currentUser, SocialContactType.LINKEDIN),
    instagram: getSocialContact(currentUser, SocialContactType.INSTAGRAM),
    x: getSocialContact(currentUser, SocialContactType.X),
    tiktok: getSocialContact(currentUser, SocialContactType.TIKTOK),
  };
}

const FIELDS_GAP: SDSFlexboxGap = 'content-large';

function CurrentUserProfileForm({
  currentUser,
  formConfig,
  formRef,
  saving,
  onSubmit,
}: {
  currentUser: CurrentUser;
  formConfig: FormConfig<FormValues>;
  formRef: RefObject<FormRef<FormValues> | null>;
  saving: boolean;
  onSubmit: (data: FormValues) => Promise<void>;
}) {
  return (
    <Form
      key={currentUser.id}
      ref={formRef}
      formConfig={formConfig}
      fieldsGap={FIELDS_GAP}
      validationSchema={userProfileFormSchema}
      isSubmitting={saving}
      onSubmit={onSubmit}
      defaultValues={buildProfileForm(currentUser)}
      submitBtnTestId={DataTestIds.PROFILE.SAVE_CHANGES_BTN}
    />
  );
}

interface UserPageProps {
  /** Member settings only; the admin app shows the same page without marketing texts. */
  showSmsMarketing?: boolean;
}

export function UserPage({ showSmsMarketing = false }: UserPageProps) {
  const { currentUser, updateUserData, updatePicture, deletePicture, saving, savingPicture } = useCurrentUser();
  const formRef = useRef<FormRef<FormValues>>(null);
  const smsActions = useRef<ProfileSmsMarketingActions>(null);
  const isSmsEnabled = useFeatureFlag(SMS_MARKETING_FEATURE_FLAG);
  const { isMobile } = useDeviceType();
  const smsFields =
    showSmsMarketing && isSmsEnabled ? (
      <ProfileSmsMarketingFields
        actionsRef={smsActions}
        isSaving={saving}
        phoneColumnStyle={getFormFieldLayoutStyle(isMobile ? '100%' : '50%', FIELDS_GAP)}
      />
    ) : undefined;

  const handleSave = async (data: FormValues) => {
    if (smsActions.current && !smsActions.current.validate()) {
      return;
    }

    const socialContacts = [
      {
        type: SocialContactType.LINKEDIN,
        value: normalizeProfileText(data.linkedin),
      },
      {
        type: SocialContactType.X,
        value: normalizeProfileText(data.x),
      },
      {
        type: SocialContactType.INSTAGRAM,
        value: normalizeProfileText(data.instagram),
      },
      {
        type: SocialContactType.TIKTOK,
        value: normalizeProfileText(data.tiktok),
      },
    ].filter(x => x.value !== '');

    const preparedData: UpdateProfileInput = {
      firstName: data.firstName,
      tagline: data.tagline,
      timezone: data.timezone || undefined,
      lastName: data.lastName,
      company: data.company,
      position: data.position,
      description: data.description,
      socialContacts,
    };

    await updateUserData(preparedData);
    await smsActions.current?.apply();
  };

  const handleSubmitFromFooter = () => {
    formRef.current?.submitForm?.();
  };

  if (!currentUser) return <Loader />;

  return (
    <div className="UserProfile">
      <div className="UserProfile__wrapper">
        <SDSFlexbox gap="no-design-32" direction="column">
          <UserProfileHeader
            profileName={currentUser.fullName}
            loading={savingPicture}
            picture={currentUser.picture}
            onSave={updatePicture}
            onDelete={deletePicture}
          />
          <CurrentUserProfileForm
            currentUser={currentUser}
            formConfig={buildUserProfileFormFields(smsFields, isMobile)}
            formRef={formRef}
            saving={saving}
            onSubmit={handleSave}
          />
        </SDSFlexbox>
      </div>
      <UserProfileFooter onClick={handleSubmitFromFooter} />
    </div>
  );
}
